import { formatDisplayNumber, getCurrencyConfig, getProjectCurrencySign } from '@stokr/components-library'

const twoDecimals = { minimumFractionDigits: 2, maximumFractionDigits: 2 }

/** Library config keys differ from ISO codes for a few currencies. */
const CONFIG_KEYS: Record<string, string> = { eur: 'euro', btc: 'bitcoin' }

/**
 * `$1,442.76`, `0.024116 XAUt`: sign and decimals from the library's currency config. A
 * currency with a symbol (`$`, `€`) is prefixed; one without (XAUt) is written after.
 */
export const formatChartMoney = (value: number, currency: string): string => {
  const key = currency.toLowerCase()
  const { sign, symbol, decimals } = getCurrencyConfig(CONFIG_KEYS[key] ?? key)
  const number = formatDisplayNumber(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return sign !== symbol ? `${sign}${number}` : `${number} ${symbol}`
}

/** `$5,371,121`: a whole amount, for totals; a currency without a sign written after it. */
export const formatWholeMoney = (value: number, currency: string): string => {
  const amount = formatDisplayNumber(value, { maximumFractionDigits: 0 })
  const sign = getProjectCurrencySign(currency)
  return sign ? `${sign}${amount}` : `${amount} ${currency}`
}

/**
 * The library's `km_ify` (`1.9K`, `6.7M`), rounded to one decimal instead of cut: 1,865.76 is 1.9K, not
 * 1.8K. The decimal is kept: 2,000 is 2.0K. Below a thousand the number itself, to `decimals` when given. `billions: false` stays in M.
 */
export const shortNumber = (
  value: number,
  { decimals, billions = true }: { decimals?: number; billions?: boolean } = {},
): string => {
  const units: [number, string][] = [...(billions ? ([[1e9, 'B']] as [number, string][]) : []), [1e6, 'M'], [1e3, 'K']]
  for (const [size, suffix] of units) {
    // Rounded first, so 999,960 reads 1M rather than 1000K.
    const scaled = Number((value / size).toFixed(1))
    if (scaled >= 1) return `${formatDisplayNumber(scaled, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${suffix}`
  }
  return decimals === undefined ? String(value) : value.toFixed(decimals)
}

/**
 * From a million up, `formatChartMoney` shortened as amounts are elsewhere (`$939.7M`); below it, in
 * full. Millions only, as the axis (`$1,243.3M`): the library's billions read "B", not "Bn".
 */
export const formatChartMoneyShort = (value: number, currency: string): string => {
  if (Math.abs(value) < 1e6) return formatChartMoney(value, currency)
  const key = currency.toLowerCase()
  const { sign, symbol } = getCurrencyConfig(CONFIG_KEYS[key] ?? key)
  const number = `${value < 0 ? '-' : ''}${shortNumber(Math.abs(value), { billions: false })}`
  return sign !== symbol ? `${sign}${number}` : `${number} ${symbol}`
}

/**
 * `30K`, `1.5M`, `1,420`, `0.0242` — value-axis labels; K and M as `shortNumber`. Below 10k in full: a NAV around 1.4k has
 * ticks 20 apart, which `1.4K` would all read alike. Below 1, three significant digits (gold units).
 */
export const formatChartCompact = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1e6) return `${formatDisplayNumber(Number((value / 1e6).toFixed(1)), { maximumFractionDigits: 1 })}M`
  if (abs >= 1e4) return `${Number((value / 1e3).toFixed(1))}K`
  if (abs >= 1) return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
  if (abs > 0 && abs < 1) return String(Number(value.toPrecision(3)))
  return String(Number(value.toFixed(2)))
}

/** `12.5%`, `-3%` — value-axis labels for returns. */
export const formatChartAxisPercent = (percent: number): string => `${Number(percent.toFixed(1))}%`

/** `+10.53%` / `-0.51%`; `signed: false` drops the `+`, for text that says "up" itself. */
export const formatChartPercent = (percent: number, signed = true): string =>
  `${signed && percent > 0 ? '+' : ''}${formatDisplayNumber(percent, twoDecimals)}%`
